import classNames from "classnames";
import { Button, Heading, Text } from "design_system";
import useLabels from "hooks/useLabels";
import { X } from "lucide-react";
import { ReactNode } from "react";

type ComposedModalRootProps = {
  children: ReactNode;
  className?: string;
  size?: "base" | "lg";
};

type ComposedModalHeaderProps = {
  title: string;
  subtitle?: ReactNode;
  onClose: () => void;
  className?: string;
  showBorder?: boolean;
};

type ComposedModalBodyProps = {
  children: ReactNode;
  fullWidth?: boolean;
  className?: string;
};

type ComposedModalFooterProps = {
  children?: ReactNode;
  primaryAction?: {
    label?: string;
    onClick: () => void;
    disabled?: boolean;
  };
  secondaryAction?: {
    label?: string;
    onClick: () => void;
  };
  className?: string;
  showBorder?: boolean;
};

/**
 * A modal component intended to be used as a wrapper for other modal components.
 * It provides a consistent layout and styling for the modal. It should be wrapped in an EmptyModal or StackedEmptyModal component.
 * TODO: Replace this with a modal wrapper in the design system
 *
 * @param props.children - The content of the modal
 * @param props.className - The class name of the modal for custom styling
 * @param props.size - The size of the modal ('base' or 'lg'). Default is 'base'.
 */
function ComposedModalRoot({
  children,
  className = "",
  size = "base",
}: ComposedModalRootProps) {
  const sizeClasses = {
    base: "sm:w-[500px] sm:max-h-[600px]",
    lg: "sm:max-w-[800px] sm:min-w-[500px] sm:max-h-[700px]",
  };

  return (
    <div
      className={classNames(
        "bg-bg-base rounded-t-xl w-full sm:rounded-b-xl max-h-[95dvh] border-box overflow-hidden flex flex-col justify-items-stretch items-stretch border sm:border-0",
        sizeClasses[size],
        className
      )}
    >
      {children}
    </div>
  );
}

/**
 * A basic header component for a modal with a title, subtitle, and close button.
 *
 * @param props.title - The title of the modal
 * @param props.subtitle - The subtitle of the modal
 * @param props.onClose - The function to call when the modal is closed
 * @param props.className - The class name of the header for custom styling
 * @param props.showBorder - Whether to show a border around the header
 */
function Header({
  title,
  subtitle,
  onClose,
  className = "",
  showBorder = true,
}: ComposedModalHeaderProps) {
  const [modalLabels] = useLabels("general");

  return (
    <div
      className={classNames(
        "py-3 pl-4 pr-1.5",
        showBorder && "border-b",
        className
      )}
    >
      <div className="flex items-stretch">
        <div className="flex flex-1 items-center">
          <Heading as="h1" size="x-sm">
            {title}
          </Heading>
        </div>
        <Button
          variant="flat"
          size="base"
          onClick={onClose}
          iconOnly
          iconLeft={<X />}
          data-testid="close-button"
        >
          {modalLabels["close"]}
        </Button>
      </div>
      {subtitle && (
        <Text as="span" size="sm" theme="muted" className="block mr-4">
          {subtitle}
        </Text>
      )}
    </div>
  );
}

/**
 * A body component for a modal to go between the header and footer.
 *
 * @param props.children - The content of the modal
 * @param props.fullWidth - Whether to make the body full width
 * @param props.className - The class name of the body for custom styling
 */
function Body({
  children,
  fullWidth = false,
  className = "",
}: ComposedModalBodyProps) {
  return (
    <div className="overflow-auto flex-1">
      <div className={classNames(fullWidth ? "my-4" : "m-4", className)}>
        {children}
      </div>
    </div>
  );
}

/**
 * A basic footer component for a modal with primary and secondary actions.
 * Secondary action defaults to "Cancel".
 *
 * @param props.children - The content of the modal
 * @param props.primaryAction - The primary action of the modal
 * @param props.secondaryAction - The secondary action of the modal
 * @param props.className - The class name of the footer for custom styling
 * @param props.showBorder - Whether to show a border around the footer
 */
function Footer({
  children,
  primaryAction,
  secondaryAction,
  className = "",
  showBorder = true,
}: ComposedModalFooterProps) {
  const [modalLabels] = useLabels("general");

  if (!children && !primaryAction && !secondaryAction) {
    return null;
  }

  return (
    <div
      className={classNames(
        "flex sticky bottom-0 flex-col-reverse gap-2 p-4 bg-bg-base sm:flex-row sm:justify-end",
        showBorder && "border-t",
        className
      )}
    >
      {children || (
        <>
          {secondaryAction && (
            <Button
              variant="tertiary"
              size="sm"
              onClick={secondaryAction.onClick}
              data-testid="cancel-button"
            >
              {secondaryAction.label || modalLabels["cancel"]}
            </Button>
          )}
          {primaryAction && (
            <Button
              variant="primary"
              size="sm"
              onClick={primaryAction.onClick}
              isDisabled={primaryAction.disabled}
            >
              {primaryAction.label || modalLabels["done"]}
            </Button>
          )}
        </>
      )}
    </div>
  );
}

const ComposedModal = Object.assign(ComposedModalRoot, {
  Header,
  Body,
  Footer,
});

export default ComposedModal;
export { ComposedModal };
